import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Image, Tooltip } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DownOutlined,
  FileTextOutlined,
  LoadingOutlined,
  ReloadOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import MemoizedMarkdown from "./MemoizedMarkdown";
import AnswerProjectionPanel from "./AnswerProjectionPanel";
import StreamElapsed, { formatElapsedSeconds } from "./StreamElapsed";
import { MarkdownCitationContext } from "./markdownContext";
import AnswerFeedback from "./AnswerFeedback";

/* One transcript row. Extracted verbatim from index.js (Wave 3.1, stage 1)
   so the row can be memoized independently of the page's ~20 useState
   values — every stream frame / keystroke / voice tick used to re-render
   and re-parse every message. The day separator stays in index.js's
   messages.map(); this component renders ONLY the <article>. */

const TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "2-digit",
});

const formatMessageTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return TIME_FORMATTER.format(date);
};

const ActivityTimelineMarker = ({ status }) => (
  <span
    className={`facility-ai-activity-marker facility-ai-activity-marker--${status}`}
    aria-hidden="true"
  >
    {status === "completed" ? (
      <CheckOutlined />
    ) : status === "running" ? (
      <LoadingOutlined />
    ) : status === "failed" || status === "stopped" ? (
      <CloseOutlined />
    ) : (
      <span className="facility-ai-activity-marker-pending" />
    )}
  </span>
);

const streamingStatusLabel = (item) => {
  const trace = item?.activity?.trace;
  const steps = Array.isArray(trace?.steps) ? trace.steps : [];
  const highlights = Array.isArray(trace?.highlights) ? trace.highlights : [];
  const active =
    steps.find((step) => step.code === trace?.activeStepCode && step.status === "running") ||
    [...steps].reverse().find((step) => step.status === "running");
  if (active?.summary) return active.summary;
  if (highlights.length) return highlights[highlights.length - 1];
  if (active?.title) return active.title;
  return "Working on this request";
};

const ActivityTimeline = ({ item, active = false }) => {
  const trace = item?.activity?.trace;
  const steps = Array.isArray(trace?.steps) ? trace.steps : [];
  const highlights = Array.isArray(trace?.highlights) ? trace.highlights : [];
  const hasRunningSteps = steps.some((step) => step.status === "running");
  /* A "running" step is only genuinely live while the message itself is
     still streaming. Server-persisted traces can carry steps stuck on
     "running" (e.g. yesterday's interrupted run); for those settled
     messages the timeline must render idle — no "Working" header, no live
     timer, and no fabricated duration. */
  const isWorking = active && hasRunningSteps;
  /* Stale "running" steps render as the neutral pending marker. */
  const displayStatus = (status) => (!active && status === "running" ? "pending" : status);
  const [timelineExpanded, setTimelineExpanded] = useState(isWorking);
  const [expandedSteps, setExpandedSteps] = useState(() => new Set());
  /* Elapsed-time tracking (3.8): messages carry no end timestamp, so the
     duration is derived from this component instance's own lifecycle — the
     wall-clock moment this timeline first saw a running step (workStartedAt,
     mirrored in a ref so the effect only depends on isWorking) is frozen
     into workedMs when the run settles. Persisted across re-renders by the
     instance; a re-run (regenerate) resets both. Historical messages loaded
     from the server never enter the working state (isWorking requires the
     message to still be streaming), so they correctly show no duration
     (no fabricated "Worked for Ns"). */
  const [workStartedAt, setWorkStartedAt] = useState(null);
  const [workedMs, setWorkedMs] = useState(null);
  const workStartRef = useRef(null);
  useEffect(() => {
    if (isWorking) {
      if (workStartRef.current == null) {
        workStartRef.current = Date.now();
        setWorkStartedAt(workStartRef.current);
        setWorkedMs(null);
      }
    } else if (workStartRef.current != null) {
      setWorkedMs(Date.now() - workStartRef.current);
      workStartRef.current = null;
      setWorkStartedAt(null);
    }
  }, [isWorking]);
  const runningChildStepCodes = active
    ? steps
        .filter((step) => step.children.length && step.status === "running")
        .map((step) => step.code)
        .join(",")
    : "";

  useEffect(() => {
    if (isWorking) {
      setTimelineExpanded(true);
    }
  }, [isWorking]);

  useEffect(() => {
    if (!runningChildStepCodes) return;
    setExpandedSteps((current) => {
      const next = new Set(current);
      runningChildStepCodes.split(",").forEach((code) => next.add(code));
      return next;
    });
  }, [runningChildStepCodes]);

  if (!steps.length) return null;

  const activeStep =
    steps.find((step) => step.code === trace?.activeStepCode) || steps[steps.length - 1];
  const activeIndex = Math.max(0, steps.findIndex((step) => step.code === activeStep.code));

  const toggleStep = (code) => {
    setExpandedSteps((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <section className="facility-ai-activity" aria-label="AI execution steps">
      <button
        type="button"
        className="facility-ai-activity-header"
        onClick={() => setTimelineExpanded((current) => !current)}
        aria-expanded={timelineExpanded}
      >
        <span className={isWorking ? "facility-ai-working-label" : undefined}>
          {isWorking ? "Working" : "Work details"}
        </span>
        <span className="facility-ai-activity-count">
          {isWorking ? `Stage ${activeIndex + 1} of ${steps.length}` : `${steps.length} steps`}
        </span>
        {/* Live 1 Hz timer while working (self-ticking — never re-renders
            this timeline); frozen duration label once settled. "Stopped
            after" for stopped/error runs, "Worked for" otherwise. */}
        {isWorking ? (
          <StreamElapsed startedAt={workStartedAt} active />
        ) : workedMs != null ? (
          <span className="facility-ai-activity-worked">
            {item.status === "stopped" || item.status === "error"
              ? `Stopped after ${formatElapsedSeconds(workedMs)}`
              : `Worked for ${formatElapsedSeconds(workedMs)}`}
          </span>
        ) : null}
        <span className="facility-ai-activity-chevron" aria-hidden="true">
          <DownOutlined rotate={timelineExpanded ? 180 : 0} />
        </span>
      </button>
      {!timelineExpanded ? (
        <button
          type="button"
          className={`facility-ai-activity-current facility-ai-activity-item--${displayStatus(activeStep.status)}`}
          onClick={() => setTimelineExpanded(true)}
          aria-expanded={false}
        >
          <ActivityTimelineMarker status={displayStatus(activeStep.status)} />
          <span className="facility-ai-activity-copy">
            <strong>{activeStep.title}</strong>
            <span>{activeStep.summary || highlights[highlights.length - 1] || ""}</span>
          </span>
          <span className="facility-ai-activity-chevron" aria-hidden="true">
            <DownOutlined rotate={-90} />
          </span>
        </button>
      ) : (
        <ol className="facility-ai-activity-list">
          {highlights.length ? (
            <li className="facility-ai-activity-highlights">
              {highlights.slice(-4).map((highlight, index) => (
                <span key={`${index}-${highlight}`}>{highlight}</span>
              ))}
            </li>
          ) : null}
          {steps.map((step) => {
            const hasChildren = step.children.length > 0;
            const isExpanded = expandedSteps.has(step.code);
            return (
              <li
                className={`facility-ai-activity-item facility-ai-activity-item--${displayStatus(step.status)}`}
                key={step.code}
              >
                <ActivityTimelineMarker status={displayStatus(step.status)} />
                <div className="facility-ai-activity-content">
                  <button
                    type="button"
                    className="facility-ai-activity-toggle"
                    onClick={() => toggleStep(step.code)}
                    aria-expanded={hasChildren ? isExpanded : undefined}
                    disabled={!hasChildren}
                  >
                    <span className="facility-ai-activity-copy">
                      <strong>{step.title}</strong>
                      <span>{step.summary}</span>
                    </span>
                    {hasChildren ? (
                      <span className="facility-ai-activity-chevron" aria-hidden="true">
                        <DownOutlined rotate={isExpanded ? 180 : -90} />
                      </span>
                    ) : null}
                  </button>
                  {hasChildren ? (
                    <div
                      className="facility-ai-activity-children"
                      hidden={!isExpanded}
                    >
                      {step.children.map((child) => (
                        <div className="facility-ai-activity-child" key={child.code}>
                          <span className={`facility-ai-activity-child-status facility-ai-activity-child-status--${displayStatus(child.status)}`} />
                          <span className="facility-ai-activity-child-copy">
                            <strong>{child.title}</strong>
                            <span>{child.summary}</span>
                          </span>
                          {child.count > 0 ? (
                            <span className="facility-ai-activity-child-count">x{child.count}</span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};

const WebSearchImage = ({ image }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <Image
      src={image.url}
      alt={image.description || "Related image from web search"}
      title={image.description || undefined}
      className="facility-ai-web-image"
      preview={{ mask: false }}
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const WebSearchImages = ({ images }) => {
  if (!Array.isArray(images) || !images.length) return null;
  return (
    <div className="facility-ai-web-images" aria-label="Related images from web search">
      <Image.PreviewGroup>
        {images.map((image) => (
          <WebSearchImage key={image.url} image={image} />
        ))}
      </Image.PreviewGroup>
    </div>
  );
};

/* React.memo comparator for transcript rows (Wave 3.1, stage 2).
   Checks, in order:
   - item reference: every stream-frame update path in index.js
     (updateStreamingMessage / appendProgressSnapshot / appendWebResults /
     appendWebImages / the setMessages reducers inside submitPrompt) maps
     over the messages array and returns the SAME object for untouched rows,
     so a single reference check covers content_markdown, status (including
     the streaming -> done transition and the "streaming" flag this row
     derives internally), activity, web_images, web_results,
     response_manifest and error_message.
   - isLast / isLastAssistant / isStreaming: booleans derived in the
     parent's messages.map(); they change this row's visuals directly
     (is-last class, regenerate button visibility/disabled state).
   - copiedMessageId: this row's UI depends only on whether copiedMessageId
     points at THIS row's id, so we compare
     (prev.copiedMessageId === prev.item.id) === (next.copiedMessageId === next.item.id)
     — transitions between other rows' ids (or null) are inert here.
   - handler identities: all three are useCallback-stable in the parent, so
     an identity change would signal a wiring mistake, not a visual change. */
const areMessageItemPropsEqual = (prev, next) =>
  prev.item === next.item &&
  prev.isLast === next.isLast &&
  prev.isLastAssistant === next.isLastAssistant &&
  prev.isStreaming === next.isStreaming &&
  prev.onCopyMessage === next.onCopyMessage &&
  prev.onRegenerateMessage === next.onRegenerateMessage &&
  prev.onOpenSources === next.onOpenSources &&
  prev.companyId === next.companyId &&
  prev.facilityId === next.facilityId &&
  (prev.copiedMessageId === prev.item.id) === (next.copiedMessageId === next.item.id);

const MessageItem = ({
  item,
  isLast,
  isLastAssistant,
  isStreaming,
  copiedMessageId,
  onCopyMessage,
  onRegenerateMessage,
  onOpenSources,
  companyId,
  facilityId,
}) => {
  /* Stable per-message citation opener (stage 2): keyed on item.id plus the
     useCallback-stable parent handler, so MarkdownCitationContext consumers
     (MarkdownLink) never re-render from a changing context value while this
     row re-renders on every stream frame. */
  const openCitation = useCallback(
    (citationId) => onOpenSources(item.id, citationId),
    [item.id, onOpenSources]
  );

  const renderSourcesButton = (sourceItem) => {
    const citations = sourceItem?.response_manifest?.citations || [];
    const webResults = Array.isArray(sourceItem.web_results) ? sourceItem.web_results : [];
    if (!citations.length && !webResults.length) return null;

    return (
      <button
        type="button"
        className="facility-ai-web-sources"
        onClick={() => onOpenSources(sourceItem.id, null)}
        aria-label={`Show ${citations.length || webResults.length} sources`}
      >
        <FileTextOutlined aria-hidden="true" />
        <span>Sources and calculations ({citations.length || webResults.length})</span>
      </button>
    );
  };

  const isMessageStreaming = item.status === "streaming";

  /* Wave 3.1 stage 4: aria-live lives ONLY on the actively streaming row
     (the list container no longer announces), so screen readers get
     per-frame updates scoped to the answer being narrated instead of
     re-reading the whole transcript's a11y tree. */
  return (
    <article
      className={`facility-ai-message facility-ai-message--${item.role} facility-ai-message--${item.status || "done"}${
        isLast ? " is-last" : ""
      }`}
      aria-live={isMessageStreaming ? "polite" : undefined}
    >
      <div className="facility-ai-message-role">
        {item.role === "assistant" ? (
          <>
            <span className="facility-ai-avatar"><RobotOutlined /></span>
            <span>Facility Assistant</span>
            {item.created_at ? (
              <span className="facility-ai-message-time">
                {formatMessageTime(item.created_at)}
              </span>
            ) : null}
          </>
        ) : (
          "You"
        )}
      </div>
      {item.role === "assistant" ? (
        <>
          <ActivityTimeline item={item} active={isMessageStreaming} />
          <AnswerProjectionPanel
            projections={item.response_manifest?.projections}
          />
          {item.content_markdown ? (
            <div className="facility-ai-message-content">
              <MarkdownCitationContext.Provider value={openCitation}>
                <MemoizedMarkdown
                  content={item.content_markdown}
                  isStreaming={isMessageStreaming}
                />
              </MarkdownCitationContext.Provider>
              {isMessageStreaming ? (
                <span className="facility-ai-stream-cursor" aria-hidden="true" />
              ) : null}
            </div>
          ) : isMessageStreaming ? (
            <div className="facility-ai-thinking" role="status">
              <span className="facility-ai-thinking-dot" />
              <span className="facility-ai-thinking-dot" />
              <span className="facility-ai-thinking-dot" />
              <span className="facility-ai-thinking-label">
                {streamingStatusLabel(item)}
              </span>
            </div>
          ) : null}
          {item.error_message ? (
            <div className={`facility-ai-message-notice facility-ai-message-notice--${item.status}`}>
              {item.error_message}
            </div>
          ) : null}
          <WebSearchImages images={item.web_images} />
          {renderSourcesButton(item)}
          {item.content_markdown && item.status !== "streaming" ? (
            <>
              <div className="facility-ai-message-actions">
                <Tooltip title={copiedMessageId === item.id ? "Copied" : "Copy answer"}>
                  <button
                    type="button"
                    className={`facility-ai-message-action${
                      copiedMessageId === item.id ? " facility-ai-message-action--copied" : ""
                    }`}
                    onClick={() => onCopyMessage(item)}
                    aria-label="Copy answer"
                  >
                    {copiedMessageId === item.id ? <CheckOutlined /> : <CopyOutlined />}
                  </button>
                </Tooltip>
                {isLastAssistant &&
                (item.status === "error" ||
                  item.status === "stopped" ||
                  item.status === "done") ? (
                  <Tooltip
                    title={
                      item.status === "done"
                        ? "Regenerate answer"
                        : "Send this question again"
                    }
                  >
                    <button
                      type="button"
                      className="facility-ai-message-action"
                      onClick={() => onRegenerateMessage(item.id)}
                      aria-label={
                        item.status === "done" ? "Regenerate answer" : "Try again"
                      }
                      disabled={isStreaming}
                    >
                      <ReloadOutlined />
                    </button>
                  </Tooltip>
                ) : null}
              </div>
              {item.status === "done" && item.access_status !== "restricted" ? (
                <AnswerFeedback item={item} companyId={companyId} facilityId={facilityId} />
              ) : null}
            </>
          ) : null}
        </>
      ) : (
        <>
          <div className="facility-ai-message-content">
            <p>{item.content_markdown}</p>
          </div>
          <div className="facility-ai-user-message-actions">
            {item.created_at ? (
              <span className="facility-ai-message-time">
                {formatMessageTime(item.created_at)}
              </span>
            ) : null}
            <Tooltip title={copiedMessageId === item.id ? "Copied" : "Copy message"}>
              <button
                type="button"
                className={`facility-ai-message-action${
                  copiedMessageId === item.id ? " facility-ai-message-action--copied" : ""
                }`}
                onClick={() => onCopyMessage(item)}
                aria-label="Copy message"
              >
                {copiedMessageId === item.id ? <CheckOutlined /> : <CopyOutlined />}
              </button>
            </Tooltip>
          </div>
        </>
      )}
    </article>
  );
};

export { formatMessageTime };
export default memo(MessageItem, areMessageItemPropsEqual);
